/**
 * User Service - Frontend
 * @description Service for user-related API operations
 */

import { api } from '../config';
import {
  ApiResponse,
  User,
  UserCreateRequest,
  UserUpdateRequest,
  UserFormData,
  UserQueryParams,
  UpdatePasswordRequest,
} from '@/types';
import { userMappers } from '@/types/mappers';

export const userService = {
  // Create a new user
  async create(formData: UserFormData): Promise<ApiResponse<User>> {
    const userData: UserCreateRequest = userMappers.toCreateRequest(formData);
    const response = await api.post<ApiResponse<User>>('/users', userData);
    return response.data;
  },

  // Get all users with optional pagination and field selection
  async getAll(
    params?: UserQueryParams & { paths?: string[]; selection?: 'association' | 'basic' | 'full' }
  ): Promise<ApiResponse<User[]>> {
    const response = await api.get<ApiResponse<User[]>>('/users', { params });
    return response.data;
  },

  // Get users for associations (optimized for minimal data)
  async getAllForAssociations(): Promise<ApiResponse<User[]>> {
    const response = await api.get<ApiResponse<User[]>>('/users', {
      params: { selection: 'association' },
    });
    return response.data;
  },

  // Get user by ID
  async getById(id: string): Promise<ApiResponse<User>> {
    const response = await api.get<ApiResponse<User>>(`/users/${id}`);
    return response.data;
  },

  // Get user by username
  async getByUsername(username: string): Promise<ApiResponse<User>> {
    const response = await api.get<ApiResponse<User>>(`/users/username/${username}`);
    return response.data;
  },

  // Update user
  async update(id: string, formData: UserFormData): Promise<ApiResponse<User>> {
    const updateRequest: UserUpdateRequest = userMappers.toUpdateRequest(formData);
    const response = await api.put<ApiResponse<User>>(`/users/${id}`, updateRequest);
    return response.data;
  },

  // Delete user (permanent)
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`/users/${id}`);
    return response.data;
  },

  // Deactivate user (soft delete)
  async deactivate(id: string): Promise<ApiResponse<void>> {
    const response = await api.patch<ApiResponse<void>>(`/users/${id}/deactivate`);
    return response.data;
  },

  // Reactivate user
  async reactivate(id: string): Promise<ApiResponse<void>> {
    const response = await api.patch<ApiResponse<void>>(`/users/${id}/reactivate`);
    return response.data;
  },

  // Get users by role
  async getByRole(role: string): Promise<ApiResponse<User[]>> {
    const response = await api.get<ApiResponse<User[]>>(`/users/role/${role}`);
    return response.data;
  },

  // Get active users
  async getActiveUsers(): Promise<ApiResponse<User[]>> {
    const response = await api.get<ApiResponse<User[]>>('/users/status/active');
    return response.data;
  },

  // Get users by custom permission
  async getByCustomPermission(permission: string): Promise<ApiResponse<User[]>> {
    const response = await api.get<ApiResponse<User[]>>(`/users/permission/${permission}`);
    return response.data;
  },

  // Update user password
  async updatePassword(
    id: string,
    passwordData: UpdatePasswordRequest
  ): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>(`/users/${id}/password`, passwordData);
    return response.data;
  },

  // Send email confirmation
  async sendEmailConfirmation(id: string): Promise<ApiResponse<void>> {
    const response = await api.post<ApiResponse<void>>(`/email/users/${id}/send-confirmation`);
    return response.data;
  },

  // Send password reset email
  async sendEmailPasswordReset(id: string): Promise<ApiResponse<void>> {
    const response = await api.post<ApiResponse<void>>(`/email/users/${id}/send-reset-password`);
    return response.data;
  },

  // Block/Deactivate user
  async blockUser(id: string): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>(`/users/${id}/block`);
    return response.data;
  },

  // Unblock/Activate user
  async unblockUser(id: string): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>(`/users/${id}/unblock`);
    return response.data;
  },

  // Manual password reset (admin action)
  async manualPasswordReset(id: string, newPassword: string): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>(`/users/${id}/manual-password-reset`, {
      newPassword,
    });
    return response.data;
  },

  // Get current user subscription info
  async getCurrentUserSubscription(): Promise<
    ApiResponse<{
      role: string;
      subscription: string;
      subscriptionExpiryDate: string | null;
    }>
  > {
    const response = await api.get<
      ApiResponse<{
        role: string;
        subscription: string;
        subscriptionExpiryDate: string | null;
      }>
    >('/users/me/subscription');
    return response.data;
  },
};
